import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  CheckCircle2,
  Circle,
  Loader2,
  Users,
  Workflow,
  FileText,
  FolderTree,
  Sparkles,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PresetApplicationResult } from '@/lib/api/services/industryPresets'

type Step = {
  id: string
  label: string
  icon: React.ReactNode
  status: 'pending' | 'in_progress' | 'completed' | 'error'
  count?: number
}

interface PresetApplicationProgressProps {
  presetName: string
  presetColor: string
  open: boolean
  isApplying: boolean
  result?: PresetApplicationResult | null
  error?: string | null
  onClose: () => void
  onGoToAgents: () => void
}

export function PresetApplicationProgress({
  presetName,
  presetColor,
  open,
  isApplying,
  result,
  error,
  onClose,
  onGoToAgents,
}: PresetApplicationProgressProps) {
  const [steps, setSteps] = useState<Step[]>([
    { id: 'agents', label: 'Creating agents', icon: <Users className="h-4 w-4" />, status: 'pending' },
    { id: 'workflows', label: 'Setting up workflows', icon: <Workflow className="h-4 w-4" />, status: 'pending' },
    { id: 'templates', label: 'Adding templates', icon: <FileText className="h-4 w-4" />, status: 'pending' },
    { id: 'folders', label: 'Creating folder structure', icon: <FolderTree className="h-4 w-4" />, status: 'pending' },
  ])
  const [currentStep, setCurrentStep] = useState(0)

  // Simulate progress when applying
  useEffect(() => {
    if (isApplying && !result && !error) {
      const interval = setInterval(() => {
        setCurrentStep((prev) => {
          if (prev >= steps.length - 1) {
            clearInterval(interval)
            return prev
          }
          
          setSteps((prevSteps) =>
            prevSteps.map((step, idx) => ({
              ...step,
              status: idx < prev + 1 ? 'completed' : idx === prev + 1 ? 'in_progress' : 'pending',
            }))
          )
          
          return prev + 1
        })
      }, 1000)

      setSteps((prevSteps) =>
        prevSteps.map((step, idx) => ({
          ...step,
          status: idx === 0 ? 'in_progress' : 'pending',
        }))
      )

      return () => clearInterval(interval)
    }
  }, [isApplying, result, error, steps.length])

  // Update steps when result is received
  useEffect(() => {
    if (result) {
      setSteps([
        {
          id: 'agents',
          label: 'Created agents',
          icon: <Users className="h-4 w-4" />,
          status: 'completed',
          count: result.createdAgents.length,
        },
        {
          id: 'workflows',
          label: 'Set up workflows',
          icon: <Workflow className="h-4 w-4" />,
          status: 'completed',
          count: result.createdWorkflows.length,
        },
        {
          id: 'templates',
          label: 'Added templates',
          icon: <FileText className="h-4 w-4" />,
          status: 'completed',
          count: result.createdTemplates.length,
        },
        {
          id: 'folders',
          label: 'Created folders',
          icon: <FolderTree className="h-4 w-4" />,
          status: 'completed',
          count: result.createdFolders.length,
        },
      ])
    }
  }, [result])

  // Update steps when error occurs
  useEffect(() => {
    if (error) {
      setSteps((prevSteps) =>
        prevSteps.map((step) => ({
          ...step,
          status: step.status === 'in_progress' ? 'error' : step.status,
        }))
      )
    }
  }, [error])

  const completedSteps = steps.filter((s) => s.status === 'completed').length
  const progress = result ? 100 : error ? (completedSteps / steps.length) * 100 : (currentStep / steps.length) * 100

  return (
    <Dialog open={open} onOpenChange={() => !isApplying && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Setup Complete!
              </>
            ) : error ? (
              <>
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Setup Failed
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 animate-pulse" style={{ color: presetColor }} />
                Setting Up {presetName}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {result
              ? `Your ${presetName} preset has been applied successfully.`
              : error
              ? `Something went wrong while applying the preset.`
              : `Please wait while we configure your workspace...`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground text-center">
              {completedSteps} of {steps.length} steps completed
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            {steps.map((step) => (
              <div
                key={step.id}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg transition-colors',
                  step.status === 'completed' && 'bg-green-50 dark:bg-green-900/20',
                  step.status === 'in_progress' && 'bg-blue-50 dark:bg-blue-900/20',
                  step.status === 'error' && 'bg-red-50 dark:bg-red-900/20'
                )}
              >
                <div className="flex-shrink-0">
                  {step.status === 'completed' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : step.status === 'in_progress' ? (
                    <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                  ) : step.status === 'error' ? (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-300 dark:text-gray-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span
                    className={cn(
                      'text-sm font-medium',
                      step.status === 'completed' && 'text-green-700 dark:text-green-400',
                      step.status === 'in_progress' && 'text-blue-700 dark:text-blue-400',
                      step.status === 'error' && 'text-red-700 dark:text-red-400',
                      step.status === 'pending' && 'text-gray-500'
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {step.count !== undefined && (
                  <Badge variant="secondary" className="text-xs">
                    {step.count}
                  </Badge>
                )}
              </div>
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Skipped items warning */}
          {result && result.skippedItems.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-400">
              <p className="font-medium mb-1">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {result.skippedItems.length} items were skipped
              </p>
              <p className="text-xs">These items already exist in your workspace.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2">
          {result ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={onGoToAgents} style={{ backgroundColor: presetColor }}>
                Go to AI Studio
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          ) : error ? (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PresetApplicationProgress
